/** Global mount point for the knowledge processing console - kept alive across route changes so the run survives minimise / navigation. */
import { Upload } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { isValidationPassed } from "./ValidationResultPanel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  JOB_PHASE,
  IN_FLIGHT_PHASES,
  DOC_INGEST_STATUS,
} from "../pages/ContentRegistry/constants";
import { ProcessingConsole } from "../pages/ContentRegistry/ProcessingConsole";
import type { ConsoleRuleCounts } from "../pages/ContentRegistry/utils";
import { useProcessingJobStore } from "../../stores/processingJobStore";

const CONTENT_REGISTRY_PATH =
  "/continuous-compliance/governance/content-registry";

export function ProcessingJobHost() {
  const navigate = useNavigate();
  const [ruleValidation, setRuleValidation] = useState<{
    documentId: string | null;
    counts: ConsoleRuleCounts | null;
  }>({ documentId: null, counts: null });
  const [confirmAction, setConfirmAction] = useState<
    "save-draft" | "cancel" | null
  >(null);
  const job = useProcessingJobStore((s) => s.job);
  const minimized = useProcessingJobStore((s) => s.minimized);
  const saving = useProcessingJobStore((s) => s.saving);
  const minimize = useProcessingJobStore((s) => s.minimize);
  const cancel = useProcessingJobStore((s) => s.cancel);
  const dismiss = useProcessingJobStore((s) => s.dismiss);
  const pickDifferent = useProcessingJobStore((s) => s.pickDifferent);
  const save = useProcessingJobStore((s) => s.save);
  const markConsoleFinished = useProcessingJobStore(
    (s) => s.markConsoleFinished,
  );
  const setStage = useProcessingJobStore((s) => s.setStage);

  if (!job) return null;

  const counts =
    ruleValidation.documentId === job.docId ? ruleValidation.counts : null;
  const passed = !!counts && counts.total > 0 && counts.failed === 0;
  const resultPassed = job.result ? isValidationPassed(job.result) : false;
  // Show results only once the console has played its closing stages and
  // no in-flight phase is still going.
  const showResults =
    job.consoleFinished &&
    !IN_FLIGHT_PHASES.includes(job.phase);
  const goPickDifferent = () => {
    pickDifferent();
    if (!window.location.pathname.startsWith(CONTENT_REGISTRY_PATH)) {
      void navigate(CONTENT_REGISTRY_PATH);
    }
  };

  const notice =
    job.phase === JOB_PHASE.ERROR && job.error
      ? { title: job.error.title, detail: job.error.message }
      : showResults && job.result && !resultPassed
        ? {
          title: "Document did not pass validation",
          detail:
            "One or more rules failed against this document. Review the failed rules in the feed, or use Back to upload a different file.",
        }
        : null;

  const canPickDifferent =
    showResults && job.phase !== JOB_PHASE.ERROR && job.mode !== "preview";
  const requestCancel = () => {
    if (job.mode === "preview") {
      void dismiss();
      return;
    }
    setConfirmAction("cancel");
  };
  const requestSave = () => {
    if (passed) {
      void save();
      return;
    }
    setConfirmAction("save-draft");
  };
  const confirmDraftSave = () => {
    setConfirmAction(null);
    void save(DOC_INGEST_STATUS.DRAFT);
  };
  const confirmCancel = () => {
    setConfirmAction(null);
    if (showResults || job.phase === JOB_PHASE.ERROR) {
      void save(DOC_INGEST_STATUS.DRAFT);
    } else {
      void cancel();
    }
  };

  const footer = (
    <div className="flex w-full flex-wrap items-center justify-end gap-2">
      <button
        type="button"
        onClick={requestCancel}
        disabled={saving}
        className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-40"
      >
        Cancel
      </button>
      {showResults &&
        job.phase !== JOB_PHASE.ERROR &&
        job.mode !== "preview" && (
          <button
            type="button"
            onClick={requestSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-bold text-white shadow-md shadow-indigo-500/20 transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-indigo-600"
          >
            <Upload className="size-3.5" />
            {saving ? "Saving & Uploading..." : "Save Document"}
          </button>
        )}
    </div>
  );

  return (
    <div className={minimized ? "hidden" : undefined}>
      <ProcessingConsole
        documentTitle={job.documentTitle}
        documentType={job.documentType}
        fileName={job.fileName}
        fileSize={job.fileSize}
        phase={job.phase}
        result={job.result}
        apiDurationMs={job.apiDurationMs}
        onMinimize={minimize}
        onCancel={requestCancel}
        onBack={canPickDifferent ? goPickDifferent : undefined}
        onFinished={markConsoleFinished}
        onStageChange={setStage}
        notice={notice}
        footer={footer}
        saving={saving}
        paused={minimized}
        pipelineStageEvents={job.pipelineStageEvents}
        layerRuns={job.layerRuns}
        domainScopeKey={job.domainScopeKey}
        onRuleCountsChange={setRuleValidation}
        variantLabel={job.mode === "preview" ? "Document knowledge Enrichment(Preview)" : undefined}
      />
      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <AlertDialogContent className="text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === "save-draft"
                ? "Save document as draft?"
                : "Save changes as draft?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === "save-draft"
                ? counts && counts.total > 0
                  ? `${counts.failed} validation ${counts.failed === 1 ? "rule has" : "rules have"} failed. Do you want to continue and save this document information as a draft?`
                  : "Validation results are not available yet. Do you want to continue and save this document information as a draft?"
                : "If you cancel without saving, the reviewed document data will be gone. Do you want to save these changes as a draft?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>
              {confirmAction === "save-draft"
                ? "Continue reviewing"
                : "Keep reviewing"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={
                confirmAction === "save-draft"
                  ? confirmDraftSave
                  : confirmCancel
              }
              disabled={saving}
            >
              Save as draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
